import { yupResolver } from '@hookform/resolvers/yup';
import Close from '@mui/icons-material/Close';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormHelperText,
	Grid,
	IconButton,
	ListItemIcon,
	ListItemText,
	MenuItem,
	Typography,
	useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useController,
	useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { useParams } from 'react-router-dom';
import { useGetAllEnumListQuery } from '@/api/common';
import { useSelector } from 'react-redux';
import { selectMyInfo } from '@/pages/auth/authSlice';
import {
	useAssignCaseWorkersMutation,
	useReturnFromICMutation,
} from '@/api/member-management/admissionRequest';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppMultipleDropDownMenu from '@/common/components/form-inputs/AppMultipleDropDownMenu';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import VisuallyHiddenInput from '@/common/components/UploadInput';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import {
	useEditCaseNoteMutation,
	useGetInterventionDetailQuery,
} from '@/api/member-management/intervention';
import { current } from '@reduxjs/toolkit';

interface Props {
	open: boolean;
	setOpen: any;
	openCurrentId: string;
}

const EditCaseNoteDialog = (props: Props) => {
	const { open, setOpen, openCurrentId } = props;
	const { t } = useTranslation();
	const [assignCaseWorkers] = useAssignCaseWorkersMutation();
	const { admissionRequestId } = useParams();
	const [files, setFiles] = useState(null);
	const [editCaseNote] = useEditCaseNoteMutation();

	const { data: allEnumList } = useGetAllEnumListQuery(null, {
		refetchOnMountOrArgChange: true,
	});

	const { data: details }: any = useGetInterventionDetailQuery(
		openCurrentId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !open,
		}
	);

	const defaultValues: DefaultValues<any> = {
		caseNoteType: -1,
	};

	const formSchema = Yup.object().shape({
		caseNoteType: Yup.string()
			.required(t('errorMessage.pleaseSelect'))
			.notOneOf(['-1'], t('errorMessage.pleaseSelect')),
		caseNoteStatus: Yup.string().when('caseNoteType', {
			is: (caseNoteType: string) => caseNoteType == '1' || caseNoteType == '2',
			then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			otherwise: (schema) => schema.nullable(),
		}),
		caseNoteDetails: Yup.string().when('caseNoteType', {
			is: (caseNoteType: string) => caseNoteType == '1',
			then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
			otherwise: (schema) => schema.nullable(),
		}),
		files: Yup.mixed().when('caseNoteType', {
			is: (caseNoteType: string) => caseNoteType === '2',
			then: (schema) =>
				schema
					.required(t('errorMessage.pleaseSelect'))
					.test('files-valid', t('errorMessage.pleaseSelect'), (value) => {
						if (value === null || value === undefined) return false;
						if (typeof value === 'string' && value.trim() === '') return false;
						return true;
					}),
			otherwise: (schema) => schema.nullable().notRequired(),
		}),
	});

	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});
	const filesField = useController({
		name: 'files',
		control: methods.control,
	});

	const watchFiles = methods.watch('files');
	const watchCaseNoteType = methods.watch('caseNoteType');

	useEffect(() => {
		if (open) {
			methods.reset(details?.data);
			if (details?.data?.caseNoteType == 2) {
				methods.setValue('files', { name: details?.data.fileName });
			}
		}
	}, [details, open]);

	const handleClose = () => {
		methods.reset({});
		setOpen(false);
	};
	const onSubmit: SubmitHandler<any> = async (data) => {
		const obj: any = { ...data, id: openCurrentId || '' };
		delete obj.files;
		const formData = new FormData();
		const appendObjToFormData = (
			formData: FormData,
			obj: any,
			parentKey = ''
		) => {
			for (const key in obj) {
				if (Object.prototype.hasOwnProperty.call(obj, key)) {
					const value = obj[key];
					const fullKey = parentKey ? `${parentKey}[${key}]` : key;

					if (value === null || value === undefined) {
						formData.append(fullKey, '');
					} else if (
						typeof value === 'object' &&
						!Array.isArray(value) &&
						value !== null
					) {
						appendObjToFormData(formData, value, fullKey);
					} else if (Array.isArray(value)) {
						value.forEach((item, index) => {
							if (typeof item === 'object' && item !== null) {
								appendObjToFormData(formData, item, `${fullKey}[${index}]`);
							} else {
								const itemValue =
									item === null || item === undefined ? '' : String(item);
								formData.append(`${fullKey}[]`, itemValue);
							}
						});
					} else {
						formData.append(fullKey, String(value));
					}
				}
			}
		};
		appendObjToFormData(formData, obj);
		if (files) {
			formData.append('files', files);
		}

		const r = await editCaseNote({ formData: formData, id: openCurrentId });

		if ('data' in r) {
			enqueueSnackbar(t('snackbar.editCaseNoteSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
		}
	};

	const handleUpload = (file: any) => {
		methods.setValue('files', file[0]);
		setFiles(file[0]);
		methods.trigger();
	};

	return (
		<>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth='sm'
				fullWidth
				onKeyDown={(e) => {
					if (e.key === 'Tab') {
						e.stopPropagation();
					}
				}}
			>
				<DialogTitle variant='h5'>{t('case.updateCaseNote')}</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={handleClose}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					<Typography variant='body2' sx={{ marginBottom: '12px' }}>
						{t('member.assignCaseWorkersText')}
					</Typography>
					<FormProvider {...methods}>
						<form onSubmit={methods.handleSubmit(onSubmit)}>
							<Grid container spacing={3} marginTop={0} marginBottom={3}>
								<Grid size={12}>
									<Typography variant='body2' color='common.neutral40'>
										{t('case.caseNoteType') + '*'}
									</Typography>
									<AppRadioGroup
										control={methods.control}
										name={`caseNoteType`}
										row
										options={(allEnumList?.data.CaseNoteTypeEnum || []).map(
											(item: any) => {
												return {
													...item,
													id: item.code,
													name: item.desc,
												};
											}
										)}
										handleChange={() => {
											// methods.trigger('caseNoteStatus');
											// methods.trigger('caseNoteDetails');
											methods.setValue('files', '');
											setFiles(null);
											methods.setValue('caseNoteStatus', '');
											methods.setValue('caseNoteDetails', '');
											methods.trigger('files');
										}}
									/>
								</Grid>
								{(watchCaseNoteType == 1 || watchCaseNoteType == 2) && (
									<Grid size={6}>
										<AppDropDownMenu
											control={methods.control}
											name='caseNoteStatus'
											label={`${t('case.caseNoteStatus')}${
												watchCaseNoteType == 1 || watchCaseNoteType == 2
													? '*'
													: ''
											}`}
											options={[
												{
													id: 1,
													name: 'Draft',
												},
												{
													id: 2,
													name: 'Submitted',
												},
											]}
										/>
									</Grid>
								)}
								{watchCaseNoteType == 1 && (
									<Grid size={12}>
										<AppTextField
											control={methods.control}
											multiline
											name='caseNoteDetails'
											label={t('case.caseNoteDetails')}
											rows={4}
										/>
									</Grid>
								)}

								{watchCaseNoteType == 2 && (
									<Grid size={12}>
										<Box sx={{ margin: '0px 0 10px' }}>
											<Typography variant='body2' color='common.neutral40'>
												{`${t('preAdmission.selectDocuments')}${
													watchCaseNoteType == 2 ? '*' : ''
												}`}
											</Typography>
											{!watchFiles && (
												<Button
													component='label'
													role={undefined}
													variant='outlined'
													tabIndex={-1}
													endIcon={<FileUploadOutlinedIcon />}
													sx={{ border: 'none' }}
												>
													{t('button.UploadFile')}
													<VisuallyHiddenInput
														type='file'
														onChange={(event: any) =>
															handleUpload(event.target.files)
														}
													/>
												</Button>
											)}
											{watchFiles && (
												<Box
													display={'flex'}
													alignItems={'center'}
													paddingLeft={'15px'}
												>
													<Typography
														variant='body1'
														sx={{
															color: '#1BBED7',
															textDecoration: 'underline',
														}}
													>
														{watchFiles.name}
													</Typography>
													<IconButton
														disabled={watchCaseNoteType != 2}
														sx={{ color: '#1BBED7' }}
														onClick={() => {
															methods.setValue('files', null);
															setFiles(null);
														}}
													>
														<CancelOutlinedIcon />
													</IconButton>
												</Box>
											)}
											<FormHelperText
												color='error'
												style={{ color: '#FF5B4D' }}
											>
												{filesField.fieldState.error
													? t('errorMessage.pleaseSelect')
													: ''}
											</FormHelperText>
										</Box>
									</Grid>
								)}
							</Grid>
						</form>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button sx={{ textTransform: 'none' }} onClick={() => handleClose()}>
						{t('button.cancel')}
					</Button>
					<Button
						type='submit'
						variant='contained'
						onClick={methods.handleSubmit(onSubmit)}
					>
						{t('button.confirm')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default EditCaseNoteDialog;
